import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { Alert } from '@shared/ui';
import { SELLER_ME_QUERY_KEY, sellerOnboardingApi } from '@modules/seller-onboarding/api';
import { sellerNeedsRevisionRegistrationWizard } from '@modules/seller-onboarding/moderation-revision';
import { sellerCanAccessPath } from '@modules/seller-staff';
import Layout from '@shared/components/Layout';
import SellerApprovedRoutes from '../../routes/SellerApprovedRoutes';
import SellerMainPage from '@pages/SellerMainPage';
import SellerRegisterPage from '@pages/SellerRegisterPage';
import SellerProfilePage from '@pages/SellerProfilePage';
import { SellerSupportChatPage } from '@pages';

function sellerApprovedCabinetBlocked(
  app: { status: string; seller?: { accountBlock?: unknown } | null } | undefined,
): boolean {
  return app?.status === 'APPROVED' && Boolean(app.seller?.accountBlock);
}

export default function SellerCabinetPage() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { data: app, isLoading, isFetching, isError, error } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
    /** Пустой/глючный первый ответ после reload лечим повтором; задержку держим маленькой, чтобы не мигал алерт. */
    retry: 3,
    retryDelay: 0,
  });

  useEffect(() => {
    const onBlocked = () => {
      void queryClient.invalidateQueries({ queryKey: SELLER_ME_QUERY_KEY });
    };
    window.addEventListener('seller-cabinet-blocked', onBlocked);
    return () => window.removeEventListener('seller-cabinet-blocked', onBlocked);
  }, [queryClient]);

  if (isLoading || (isFetching && !app)) {
    return (
      <Center mih="60vh">
        <Loader />
      </Center>
    );
  }

  if (isError && !app) {
    return (
      <Center mih="60vh" p="md">
        <Alert tone="error" title="Не удалось загрузить заявку" maw={480}>
          {(error as Error)?.message || 'Обновите страницу или войдите снова.'}
        </Alert>
      </Center>
    );
  }

  if (!app) {
    return (
      <Center mih="60vh">
        <Loader />
      </Center>
    );
  }

  const path = location.pathname;

  if (app.status === 'APPROVED' && !sellerApprovedCabinetBlocked(app)) {
    if (!sellerCanAccessPath(app, path)) {
      return <Navigate to="/" replace />;
    }
    return <SellerApprovedRoutes />;
  }

  if (sellerApprovedCabinetBlocked(app)) {
    const allowedApprovedBlocked =
      path === '/' ||
      path === '' ||
      path === '/profile' ||
      path.startsWith('/support-chat');
    if (!allowedApprovedBlocked) {
      return <Navigate to="/" replace />;
    }
    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<SellerMainPage />} />
          <Route path="profile" element={<SellerProfilePage />} />
          <Route path="support-chat" element={<SellerSupportChatPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  const allowedCabinetPaths =
    path === '/' ||
    path === '' ||
    path === '/registration' ||
    path === '/profile' ||
    path.startsWith('/support-chat');
  if (!allowedCabinetPaths) {
    return <Navigate to="/" replace />;
  }

  if (app.status === 'DRAFT') {
    return <SellerRegisterPage />;
  }

  if (app.status === 'PENDING_MODERATION' || app.status === 'REJECTED') {
    const showRevisionRegistration = sellerNeedsRevisionRegistrationWizard(
      app.status,
      app.moderationRejectionReason,
      app.moderationRejectionKind,
    );
    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<SellerMainPage />} />
          <Route path="profile" element={<SellerProfilePage />} />
          <Route path="support-chat" element={<SellerSupportChatPage />} />
          {showRevisionRegistration ? (
            <Route path="registration" element={<SellerRegisterPage />} />
          ) : null}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    );
  }

  return null;
}
